"use client";
import { cn } from "@/core/lib/utils";
import type { UseFormRegister, FieldValues, Path } from "react-hook-form";
import { WidgetFormData } from "@/core/schemas/widgetSchema";

type Props<TFieldValues extends FieldValues = WidgetFormData> = {
  label?: string;
  name: Path<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  errors?: Record<string, unknown>;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  rows?: number;
  inputClassName?: string;
  labelClassName?: string;
  className?: string
  variant?: "default" | "borderless";
  required?: boolean;
};

export default function TextareaInput<
  TFieldValues extends FieldValues = WidgetFormData
>({
  label,
  name,
  register,
  errors,
  placeholder,
  disabled = false,
  readOnly = false,
  rows = 3,
  inputClassName = "",
  labelClassName,
  className,
  variant = "default",
  required = false
}: Props<TFieldValues>) {
  const getNestedError = (name: string, errors?: Record<string, unknown>) =>
    name.split(".").reduce<unknown>((acc, key) => {
      if (acc && typeof acc === "object" && key in acc) {
        return (acc as Record<string, unknown>)[key];
      }
      return undefined;
    }, errors);

  const error = getNestedError(name, errors) as
    | { message?: string }
    | undefined;

  return (
    <div
      className={cn(
        "relative flex flex-col justify-start",
        variant === "default" ? "gap-2" : "",
        className
      )}
    >
      {label && (
        <label className={cn("text-primary-600 font-bold", labelClassName)} htmlFor={name}>
          {label} {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <textarea
        {...register(name)}
        id={name}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        className={cn(
          "text-sm border border-primary-300 min-h-18 placeholder-primary-500 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-0 focus:border-primary-700 resize-y",
          error ? "border-red-600" : "",
          variant === "default" ? "rounded-md py-2 px-3" : "border-b",
          inputClassName,
        )}
      />
      {error?.message && <p className="text-red-600">{error.message}</p>}
    </div>
  );
}
